import React from "react";
import { PATH_DIRECTIONS_HOURS } from "../utils/constants";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { MapPinIcon, PhoneIcon, EnvelopeIcon, ClockIcon } from "@heroicons/react/24/outline";
import { LogoSvg } from "../components/svgs/LogoSvg";

export default function DirectionsHoursPage() {
  return (
    <>
      <SEOHelmet page="anfahrt-oeffnungszeiten" pathname={PATH_DIRECTIONS_HOURS} />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Ergotherapie Rottweil - Lisa Spreitzer",
          "telephone": "+49-741-89698293",
          "email": "info@ergorw.de",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Auf dem Wall 29",
            "addressLocality": "Rottweil",
            "postalCode": "78628",
            "addressCountry": "DE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "48.1684",
            "longitude": "8.6278"
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              "opens": "08:00",
              "closes": "18:00"
            },
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": "Saturday",
              "opens": "07:00",
              "closes": "17:00"
            }
          ],
          "url": "https://ergorw.de"
        })}
      </script>

      <main className="space-y-12 sm:space-y-24">
        <section className="mx-auto px-6 lg:px-8" aria-label="Kontakt & Anfahrt">
          <header className="mb-10 sm:mb-14">
            <div className="mx-auto text-center">
              <div className="flex flex-col items-center">
                <LogoSvg
                  classNames="h-16 w-auto fill-current text-ergo-500 dark:text-ergo-400 mb-8"
                  aria-label="Ergotherapie Rottweil Logo"
                />
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6">
                  Kontakt & Anfahrt
                </h1>
              </div>
            </div>
          </header>

          <div className="mx-auto max-w-2xl">
            <div className="bg-white dark:bg-gray-800/60 rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-200/60 dark:border-gray-700/40">
              <div className="space-y-4 text-base text-gray-600 dark:text-gray-300">
                {/* Adresse */}
                <div className="flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                  <div className="mt-1 rounded-lg bg-ergo-50 text-ergo-600 dark:bg-ergo-900/50 dark:text-ergo-400 p-2">
                    <MapPinIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Adresse</h3>
                    <a
                      href="https://www.google.com/maps/dir/?api=1&destination=Auf%20dem%20Wall%2029%2C%2078628%20Rottweil"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors"
                    >
                      Auf dem Wall 29
                    </a>
                    <span>78628 Rottweil</span>
                  </div>
                </div>

                {/* Öffnungszeiten */}
                <div className="flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                  <div className="mt-1 rounded-lg bg-ergo-50 text-ergo-600 dark:bg-ergo-900/50 dark:text-ergo-400 p-2">
                    <ClockIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Öffnungszeiten</h3>
                    <p>Mo-Fr: 08.00-18.00</p>
                    <p>Sa: 07.00-17.00</p>
                  </div>
                </div>

                {/* Telefon */}
                <div className="flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                  <div className="mt-1 rounded-lg bg-ergo-50 text-ergo-600 dark:bg-ergo-900/50 dark:text-ergo-400 p-2">
                    <PhoneIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Telefon</h3>
                    <a href="tel:+4974189698293" className="hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors">
                      0741 89 69 82 93
                    </a>
                  </div>
                </div>

                {/* E-Mail */}
                <div className="flex items-start gap-4 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
                  <div className="mt-1 rounded-lg bg-ergo-50 text-ergo-600 dark:bg-ergo-900/50 dark:text-ergo-400 p-2">
                    <EnvelopeIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">E-Mail</h3>
                    <a href="mailto:info@ergo-rw.de" className="hover:text-ergo-600 dark:hover:text-ergo-400 transition-colors">
                      info@ergo-rw.de
                    </a>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Auf%20dem%20Wall%2029%2C%2078628%20Rottweil"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full px-5 py-2 text-sm font-medium text-ergo-700 bg-ergo-50 hover:bg-ergo-100 dark:text-ergo-300 dark:bg-ergo-900/40 dark:hover:bg-ergo-900/60 transition-colors"
                >
                  Route planen
                </a>
                <a
                  href="tel:+4974189698293"
                  className="rounded-full px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800/60 dark:hover:bg-gray-800/80 transition-colors"
                >
                  Jetzt anrufen
                </a>
                <a
                  href="mailto:info@ergo-rw.de"
                  className="rounded-full px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800/60 dark:hover:bg-gray-800/80 transition-colors"
                >
                  E-Mail schreiben
                </a>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-5xl mt-12 sm:mt-16">
            <div className="w-full aspect-[4/3] sm:aspect-[3/2] rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800">
              <iframe
                title="Anfahrt zur Praxis"
                className="w-full h-full border-0"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2661.6776429317878!2d8.62733297688778!3d48.16862197123133!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479764c9178af4d7%3A0x832f5e38c16d5c49!2sAuf%20dem%20Wall%2029%2C%2078628%20Rottweil!5e0!3m2!1sde!2sde!4v1693516317714!5m2!1sde!2sde&style=feature:all|element:geometry|color:0x212121&style=feature:all|element:labels.text.stroke|color:0x000000&style=feature:all|element:labels.text.fill|color:0x757575&style=feature:poi|element:labels.text.fill|color:0x757575&style=feature:poi.park|element:geometry|color:0x181818&style=feature:poi.park|element:labels.text.fill|color:0x616161&style=feature:road|element:geometry.fill|color:0x2c2c2c&style=feature:road|element:labels.text.fill|color:0x8a8a8a&style=feature:road.arterial|element:geometry|color:0x373737&style=feature:road.highway|element:geometry|color:0x3c3c3c&style=feature:road.highway.controlled_access|element:geometry|color:0x4e4e4e&style=feature:road.local|element:labels.text.fill|color:0x616161&style=feature:transit|element:labels.text.fill|color:0x757575&style=feature:water|element:geometry|color:0x000000&style=feature:water|element:labels.text.fill|color:0x3d3d3d"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </section>
      </main>
    </>
  );
}


